import { useEffect, useMemo, useState } from "react";
import "styles/ns-loader.css";

/* Brand thinking state. One motion per wait — never add a spinner beside it.

   Waiting lines are short, plain, one dry beat, and aimed at the paperwork.
   Never a joke on an error or a privacy state, so `errorCopy` handles those and
   this never appears there. */

export const WRITING_LABELS = [
  "Writing it up",
  "Turning talk into notes",
  "Putting it in order",
  "Getting it down",
  "Making sense of it",
  "Shaping the note",
];

/** Shown once a wait passes LONG_WAIT_MS. Swap the words, never the motion. */
export const LONG_WAIT_LABEL = "Still going. Worth the wait.";
const LONG_WAIT_MS = 6000;
const ROTATE_MS = 2600;

const SIZES = { md: "", sm: "ns-loader--sm" };

const pickStart = (labels) => Math.floor(Math.random() * labels.length);

const NsLoader = ({
  label,
  labels = WRITING_LABELS,
  rotate = true,
  size = "md",
  dots = true,
  dark = false,
  bare = false,
  className = "",
}) => {
  // Start somewhere different each time so the same line does not greet
  // every generation.
  const [index, setIndex] = useState(() => pickStart(labels));
  const [isLongWait, setIsLongWait] = useState(false);

  const isFixed = Boolean(label);

  useEffect(() => {
    if (isFixed) return undefined;
    const timer = setTimeout(() => setIsLongWait(true), LONG_WAIT_MS);
    return () => clearTimeout(timer);
  }, [isFixed]);

  useEffect(() => {
    if (isFixed || !rotate || isLongWait) return undefined;
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % labels.length),
      ROTATE_MS
    );
    return () => clearInterval(timer);
  }, [isFixed, rotate, isLongWait, labels.length]);

  const shown = isFixed ? label : isLongWait ? LONG_WAIT_LABEL : labels[index % labels.length];

  const classes = useMemo(
    () =>
      ["ns-loader", SIZES[size] ?? "", dark ? "ns-loader--dark" : "", bare ? "ns-loader--bare" : "", className]
        .filter(Boolean)
        .join(" "),
    [size, dark, bare, className]
  );

  return (
    <div className={classes} role="status" aria-live="polite">
      <img className="ns-loader__icon" src="/brand-mark.png" alt="" aria-hidden="true" />
      {/* keyed so the cross-fade replays when the words change */}
      <span className="ns-loader__label" key={shown}>
        {shown}
      </span>
      {dots && (
        <span className="ns-loader__dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      )}
    </div>
  );
};

export default NsLoader;
